// Pomona College: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Pomona's Common Data Set 2025-2026, published as a Tableau workbook
//    ("CDS 2025-26") linked from Pomona's Institutional Research page. Read
//    through the workbook's own PDF and CSV exports of section C: C1
//    applicants/admits/enrollees (Fall 2025; the sheet prints no year, and
//    its counts differ from the 2024-25 workbook's), C21 early decision
//    counts. Some labels in the workbook still carry last year's dates (C8A
//    says "Fall 2026", C9 "Fall 2024", G0 "2025-2026 costs"); its G1 figures
//    match the college's 2026-2027 cost page exactly. C1 has no residency
//    table.
//  - pomona.edu: Paths to Apply (plans, dates, binding terms, test policy)
//    and Cost of Attendance (2026-2027). The dates table prints no year; it
//    is the page live for the cycle now open.
//  - College Scorecard, school id 121345 (net price by family income), read
//    from the Scorecard site's own API response.
// Nothing here is profile-derived or anyone's odds. Re-check a
// figure against its source before editing it, and move `lastChecked` when
// you do.

export const SOURCES = {
    cds: {
        label: 'Pomona Common Data Set 2025-2026',
        url: 'https://tableau.campus.pomona.edu/views/CDS2025-26/C_First-timeFirst-yearadmission',
        read: '2026-09-30',
    },
    apply: {
        label: 'Pomona Admissions, Paths to Apply',
        url: 'https://www.pomona.edu/admissions/paths-apply',
        read: '2026-09-30',
    },
    cost: {
        label: 'Pomona Financial Aid, Cost of Attendance',
        url: 'https://www.pomona.edu/financial-aid/cost-attendance',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?121345-Pomona-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'pomona-college',
    kbId: 'pomona_college',
    name: 'Pomona College',
    shortName: 'Pomona',
    location: 'Claremont, CA',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Pomona graphic standards); decoration only.
    branding: {
        primary: '#0057B8',
        secondary: '#0057B8',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/pomona_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 12470,
        admitted: 890,
        enrolled: 421,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Pomona\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early decision', applied: 1656, admitted: 227 },
        ],
        note: 'The Common Data Set gives one early decision count; it does not split Early Decision I from Early Decision II.',
        source: 'cds',
        where: 'section C21',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 8' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 8', decision: 'By December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 8', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 8', decision: 'By April 1', binding: false },
        ],
        note: 'Reply dates: January 1 for Early Decision I, March 1 for Early Decision II, May 1 for Regular Decision. Transcripts and recommendation letters do not have to arrive by the deadline, though Pomona asks for them as close to it as you can. Pomona has no early action plan.',
        source: 'apply',
        binding: 'Early decision is binding: if you are admitted, you agree to attend and to withdraw all your other applications. You may still apply elsewhere under non-binding, unrestricted plans, including early action.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Pomona is test-optional. The application asks whether you want to submit scores; if you say yes, you report them in your applicant portal and they are read. If you say no, scores are not considered, even if a testing agency sends them or they appear on your transcript. If you send the ACT, Pomona requires the Core ACT plus the Science section.',
        source: 'apply',
        cdsLine: 'The Common Data Set agrees: the SAT or ACT is not required for admission but is considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71660 },
            { item: 'Fees', amount: 420 },
            { item: 'Housing and food (on campus)', amount: 23590 },
        ],
        total: 95670,
        note: 'These are the billed costs. Pomona adds $1,500 to $2,600 for travel and personal costs, and student health insurance is $3,312 unless you waive it with comparable coverage. From fall 2026, books and supplies are provided at no extra charge.',
        source: 'cost',
        netPrice: {
            average: 18978,
            byIncome: [
                { income: '$0 to $30,000', amount: 5297 },
                { income: '$30,001 to $48,000', amount: 5418 },
                { income: '$48,001 to $75,000', amount: 5790 },
                { income: '$75,001 to $110,000', amount: 16466 },
                { income: 'Over $110,000', amount: 48928 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Pomona\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
